import './style.css'
import { Position, ResizableDelta, Rnd } from 'react-rnd'
import { useAppDispatch } from '../../hooks/useAppDispatch'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark'
import { useAppSelector } from '../../hooks/useAppSelector'
import {
    getDescriptionWindowCoordinates,
    getDescriptionWindowData,
    getDescriptionWindowSize,
    getDescriptionWindowState,
} from '../../store/selectors/descriptionWindowSelectors'
import { NoData } from '../NoData'
import DOMPurify from 'dompurify'
import { ResizeDirection } from 're-resizable'
import { DraggableData, DraggableEvent } from 'react-draggable'
import { descriptionWindowActions } from '../../store/slices/descriptionWindowSlice'

export const DescriptionWindow = () => {
    const dispatch = useAppDispatch()

    const [isDragging, setIsDragging] = useState(false)
    const coordinates = useAppSelector(getDescriptionWindowCoordinates)
    const show = useAppSelector(getDescriptionWindowState)
    const size = useAppSelector(getDescriptionWindowSize)
    const data = useAppSelector(getDescriptionWindowData)
    const { header, description: descriptionDataDirty } = data

    const isString = typeof descriptionDataDirty === 'string'
    const sanitizedHTML = isString
        ? DOMPurify.sanitize(descriptionDataDirty, {
              USE_PROFILES: { html: true },
          })
        : ''

    const onResizeStop = (
        _e: MouseEvent | TouchEvent,
        _dir: ResizeDirection,
        ref: HTMLElement,
        _delta: ResizableDelta,
        position: Position
    ) => {
        dispatch(
            descriptionWindowActions.changeSize({
                width: ref.offsetWidth,
                height: ref.offsetHeight,
            })
        )
        dispatch(descriptionWindowActions.changeCoordinates(position))
    }

    const onDragStop = (_e: DraggableEvent, data: DraggableData) => {
        setIsDragging(false)
        dispatch(
            descriptionWindowActions.changeCoordinates({ x: data.x, y: data.y })
        )
    }

    const onClose = () => {
        dispatch(descriptionWindowActions.hide())
    }

    if (!show) return null

    return (
        <Rnd
            default={{
                x: coordinates.x || 100,
                y: coordinates.y || 100,
                width: size.width || 300,
                height: size.height || 500,
            }}
            minWidth={200}
            minHeight={150}
            bounds="window"
            dragHandleClassName="modal-header"
            style={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                zIndex: 10000,
                display: 'flex',
                flexDirection: 'column',
                cursor: isDragging ? 'grabbing' : 'default',
            }}
            onResizeStop={onResizeStop}
            onDragStop={onDragStop}
            onDragStart={() => setIsDragging(true)}
        >
            <div
                className="modal-header"
                style={{
                    padding: '15px 20px',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    borderBottom: '1px solid #ccc',
                    cursor: isDragging ? 'grabbing' : 'grab',
                }}
            >
                <h5
                    className="m-0 text-capital"
                >
                    {header}
                </h5>
                <button
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#606060',
                        cursor: 'pointer',
                    }}
                    onClick={onClose}
                >
                    <FontAwesomeIcon
                        icon={faXmark}
                        size="1x"
                    />
                </button>
            </div>
            <div
                style={{
                    padding: '20px',
                    overflowY: 'auto',
                    flex: 1,
                }}
            >
                {descriptionDataDirty ? (
                    <div dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />
                ) : (
                    <NoData style={{ height: '50%' }} />
                )}
            </div>
            <div style={{ padding: '10px' }} />
        </Rnd>
    )
}
